import { useTranslation } from "react-i18next";
import InlineRetry from "../../components/forms/InlineRetry";
import MultiSelect from "../../components/forms/MultiSelect";
import { isUnscoped, rubricNeedsSubject, SUBJECT_SCOPES } from "../../utils/helpers/standardScope";
import { isScopeChanged } from "./templateTree";

// Which lessons a standard is scored on: every subject, only the listed ones,
// or all but the listed ones. Shared by the tree editor and the wording editor,
// where it is the one structural change a used version takes in place. Reads
// and emits the editor state's { subjectScope, subjects } shape; `catalog` is
// useDomainSubjects(), seeded with the version's subjects so every chip is
// named. Given `stored`, the scope as saved on a published version, a change
// says what it does to past lessons.
export default function SubjectScopeField({
  subjectScope,
  subjects,
  catalog,
  onChange,
  stored = null,
  invalid = false,
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 shrink-0">
          {t("subjectScope.label")}
        </span>
        <div
          role="radiogroup"
          aria-label={t("subjectScope.label")}
          className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg shrink-0 self-start sm:self-auto"
        >
          {SUBJECT_SCOPES.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={subjectScope === value}
              onClick={() => onChange({ subjectScope: value, subjects })}
              className={`px-3 py-1 rounded-md text-[11px] font-bold transition ${
                subjectScope === value
                  ? "bg-white text-indigo-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {t(`subjectScope.${value}`)}
            </button>
          ))}
        </div>
        {subjectScope !== "all" &&
          (catalog.failed ? (
            <p role="alert" className="flex-1 flex items-center gap-3 text-[11px] font-semibold text-rose-600">
              {t("subjectScope.loadFailed")}
              <InlineRetry onRetry={catalog.reload} className="" />
            </p>
          ) : (
            <div
              className={`flex-1 min-w-0 rounded-xl ${invalid ? "ring-2 ring-rose-500/30" : ""}`}
            >
              <MultiSelect
                options={catalog.options}
                value={subjects}
                onChange={(next) => onChange({ subjectScope, subjects: next })}
                placeholder={t("subjectScope.pick")}
                emptyText={t("common.subjectsNoMatch")}
              />
            </div>
          ))}
      </div>
      {/* Past lessons scored on this standard are read through the new scope:
          one with no subject recorded falls outside every "only" list. */}
      {stored && isScopeChanged({ subjectScope, subjects }, stored) && (
        <p className="text-[11px] font-semibold text-amber-700">
          {t(`subjectScope.${subjectScope}Past`)}
        </p>
      )}
    </div>
  );
}

// The first scoped standard makes every observation on a published version —
// at every school using it — ask for the lesson subject. Said before the save:
// `stored` is the version's API standards, `standards` the edited editor state.
export function SubjectNowRequired({ stored, standards }) {
  const { t } = useTranslation();
  if (rubricNeedsSubject(stored) || standards.every((s) => isUnscoped(s.subjectScope))) {
    return null;
  }
  return (
    <p className="mt-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-200 text-[11px] font-semibold text-amber-800">
      {t("subjectScope.nowRequired")}
    </p>
  );
}
